import { ResponseStatusEnum } from "@clockwise/schema";
import { getFullName, Profile } from "@clockwise/web-commons/src/util/profile.util";
import { Avatar, Tooltip, Badge } from "@material-ui/core";
import { CheckCircle, Cancel } from "@material-ui/icons";
import classNames from "classnames";
import React, { useMemo } from "react";
import { LogoClock } from "@components/logo";

export type Props = {
  profile: Profile;
  size: "small" | "medium" | "large" | "xlarge";
  border?: boolean;
  "cw-id"?: string;
  "aria-label"?: string;
  rsvp?: ResponseStatusEnum | null;
  onClick?: () => void;
  tooltip?: React.ReactNode;
  showUserBadge?: boolean;
};

export const SIZE_TABLE = {
  small: "24px",
  medium: "30px",
  large: "40px",
  xlarge: "75px",
} as const;

export function UserAvatar({
  profile,
  size,
  "cw-id": cwId,
  "aria-label": ariaLabel,
  border = false,
  rsvp,
  onClick,
  tooltip,
  showUserBadge = false,
}: Props) {
  const badgeContent = useMemo(() => {
    // Make badge height and width a function of the avatar size, with a max of 20.
    const dimension = Math.min(Math.floor(parseInt(SIZE_TABLE[size]) / 1.6), 26);
    if (rsvp !== undefined && rsvp !== null) {
      if (rsvp === ResponseStatusEnum.Accepted) {
        return (
          <CheckCircle
            style={{ height: dimension, width: dimension }}
            className="cw-fill-green-darker cw-bg-white cw-rounded-full"
          />
        );
      }
      if (rsvp === ResponseStatusEnum.Tentative) {
        return (
          <CheckCircle
            style={{ height: dimension, width: dimension }}
            className="cw-fill-yellow-standard cw-bg-white cw-rounded-full"
          />
        );
      }
      if (rsvp === ResponseStatusEnum.Declined) {
        return (
          <Cancel
            style={{ height: dimension, width: dimension }}
            className="cw-fill-red-standard cw-bg-white cw-rounded-full"
          />
        );
      }
    } else if (showUserBadge) {
      return (
        <div style={{ height: dimension - 3, width: dimension - 3, marginTop: -4, marginLeft: -4 }}>
          <LogoClock className="cw-bg-blue-standard cw-rounded-full cw-p-0.5" />
        </div>
      );
    }
    return null;
  }, [rsvp, showUserBadge, size]);

  const { givenName, familyName, externalImageUrl } = profile;
  if (!externalImageUrl && !givenName && !familyName) {
    return null;
  }

  const fullName = getFullName(profile);
  return (
    <Tooltip title={tooltip || fullName}>
      <Badge
        overlap="circular"
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        showZero={false}
        badgeContent={badgeContent}
        className="cw-p-0"
      >
        <Avatar
          alt={fullName}
          src={externalImageUrl || undefined}
          style={{
            width: SIZE_TABLE[size],
            height: SIZE_TABLE[size],
          }}
          className={classNames(
            "cw-text-white cw-bg-gradient-to-br cw-from-blue-standard cw-to-green-standard",
            border && "cw-border-2 cw-border-white cw-border-solid",
            size === "small" && "cw-text-14",
            size === "medium" && "cw-text-16",
            size === "large" && "cw-text-20",
            size === "xlarge" && "cw-text-32",
          )}
          cw-id={cwId}
          aria-label={ariaLabel || `${fullName || "user"} avatar`}
          onClick={onClick}
        >
          {fullName[0]?.toUpperCase()}
        </Avatar>{" "}
      </Badge>
    </Tooltip>
  );
}
